import React from "react";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { InfoOutlined } from "@mui/icons-material";
import { ApprovalButton, OutlinedButton } from "@/Components/v2/atoms/Button";
import { ActionPlanSummary } from "@mutiny-group/sbo-openapi-client";
import { Select } from "@/Components/v2/atoms/Select";
import { MenuItem } from "@/Components/v2/atoms/MenuItem";
import { truncate } from "lodash";
import { SelectChangeEvent, useTheme } from "@mui/material";

type BannerStyle = {
  title: string;
  description: string;
  style: {
    infoIcon: {
      color: string;
    };
  };
};

interface ApproveManyActionBannerProps {
  onViewClick: () => void;
  onApproveClick: () => void;
  onCreateClick: () => void;
  setEligibleActionPlan: (actionPlan: ActionPlanSummary) => void;
  eligibleActionPlans: ActionPlanSummary[];
  eligibleActionPlan: ActionPlanSummary | null;
  bannerStyle: BannerStyle;
}

const ActionPlanSelector = ({
  eligibleActionPlans,
  eligibleActionPlan,
  setEligibleActionPlan,
}: {
  eligibleActionPlans: ActionPlanSummary[];
  eligibleActionPlan: ActionPlanSummary | null;
  setEligibleActionPlan: (actionPlan: ActionPlanSummary) => void;
}) => {
  const theme = useTheme();
  const handleSelectActionPlan = (event: SelectChangeEvent<string>) => {
    setEligibleActionPlan(
      eligibleActionPlans.find((plan) => plan.uuid === event.target.value) as ActionPlanSummary
    );
  };
  return (
    <Select
      value={eligibleActionPlan?.uuid}
      onChange={handleSelectActionPlan}
      displayEmpty
      sx={(theme) => ({
        height: "40px",
        width: "310px",
        boxSizing: "border-box",
        backgroundColor: theme.palette.mode === "dark" ? "#242423" : theme.palette.common.white,
        "& .MuiSelect-select": {
          color: eligibleActionPlan?.uuid ? "text.primary" : "grey.500",
        },
      })}
      renderValue={(selected) => {
        if (!selected) {
          return (
            <Typography sx={{ color: "text.primary", fontSize: "14px" }}>
              Select an Action Plan
            </Typography>
          );
        }
        return truncate(eligibleActionPlan?.name, { length: 32 });
      }}
    >
      {eligibleActionPlans.map((option) => (
        <MenuItem key={option.uuid} value={option.uuid}>
          {truncate(option.name, { length: 48 })}
        </MenuItem>
      ))}
    </Select>
  );
};
const ApproveManyActionBanner = ({
  onViewClick,
  onApproveClick,
  onCreateClick,
  setEligibleActionPlan,
  eligibleActionPlans,
  eligibleActionPlan,
  bannerStyle,
}: ApproveManyActionBannerProps) => {
  const disableButtons = !eligibleActionPlan;
  return (
    <Stack direction="column" spacing={2} alignItems="center">
      <Stack direction="row" spacing={1}>
        <InfoOutlined sx={{ ...bannerStyle.style.infoIcon, fontSize: 24 }} />
        <Stack>
          <Typography variant="body2" fontWeight={600} mb={0.5}>
            {bannerStyle.title}
          </Typography>
          <Typography variant="body2">{bannerStyle.description}</Typography>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          boxSizing: "border-box",
          padding: "0px 32px",
        }}
      >
        <OutlinedButton onClick={onCreateClick} sx={{ whiteSpace: "nowrap" }}>
          Create Action Plan
        </OutlinedButton>
        <Typography variant="body2" fontWeight={600} mb={0.5}>
          Or
        </Typography>
        <ActionPlanSelector
          eligibleActionPlans={eligibleActionPlans}
          eligibleActionPlan={eligibleActionPlan}
          setEligibleActionPlan={setEligibleActionPlan}
        />
        <OutlinedButton
          onClick={onViewClick}
          sx={{ whiteSpace: "nowrap" }}
          disabled={disableButtons}
        >
          View Action Plan
        </OutlinedButton>

        <ApprovalButton
          onClick={onApproveClick}
          sx={{ whiteSpace: "nowrap" }}
          disabled={disableButtons}
        >
          Approve For Action Plan
        </ApprovalButton>
      </Stack>
    </Stack>
  );
};

export default ApproveManyActionBanner;
